import React from 'react';
import { Platform } from 'react-native';
import { DefaultTheme, ThemeProvider } from '@react-navigation/native';
import { Stack } from 'expo-router';

import { OrderStatusBar } from '../components';
import { NoticeBannersOverlay } from '../components/NoticeBannersOverlay';
import { useQuickActions } from '../hooks';
import {
  APP_BACKGROUND,
  DEFAULT_SCREEN_OPTIONS,
  useCancelReorderOnModalExit,
  useCloseNoticeBannersOnModalExit,
  useTabStackOptions,
  useTrackRoutes,
} from '../routing';

// ─────────────────────────────────────────────────────────────────────────────

export default function RootLayout() {
  const options = useTabStackOptions();

  // ─── Effects ─────────────────────────────────────────────────────────

  useTrackRoutes();
  useQuickActions();
  useCloseNoticeBannersOnModalExit();
  useCancelReorderOnModalExit();

  // ─────────────────────────────────────────────────────────────────────

  return (
    <ThemeProvider value={NAVIGATION_THEME}>
      <Stack initialRouteName="(tabs)" screenOptions={SCREEN_OPTIONS}>
        <Stack.Screen name="(tabs)" options={options.Tabs} />
        <Stack.Screen name="(auth)" options={options.Auth} />
        <Stack.Screen name="(modals)" options={options.Modals} />
        <Stack.Screen name="+not-found" options={options.NotFound} />
      </Stack>

      {/* NOTE: On iOS the status bar is controlled by React Navigation */}
      {Platform.OS === 'android' ? <OrderStatusBar /> : null}

      <NoticeBannersOverlay />
    </ThemeProvider>
  );
}

// ─── Constants ───────────────────────────────────────────────────────────────

/**
 * `@react-navigation/elements` v2 expects `fonts` in the theme, but
 * `@react-navigation/native` v6's `DefaultTheme` does not include it.
 *
 * We extend the default theme with `fonts` to prevent crashes in
 * components like `HeaderTitle` that access `theme.fonts.bold`.
 */
const NAVIGATION_THEME = {
  ...DefaultTheme,
  colors: {
    ...DefaultTheme.colors,
    background: APP_BACKGROUND,
  },
  fonts: {
    regular: { fontFamily: 'SweetSans Text', fontWeight: '400' as const },
    medium: { fontFamily: 'SweetSans Text', fontWeight: '500' as const },
    bold: { fontFamily: 'SweetSans Text', fontWeight: '700' as const },
    heavy: { fontFamily: 'SweetSans Text', fontWeight: '900' as const },
  },
};

const SCREEN_OPTIONS = {
  ...DEFAULT_SCREEN_OPTIONS,
  title: 'sweetgreen',
};
